import { t } from 'i18next';
import { useMemo } from 'react';
import { useSettings } from '../../../context/settings-context';
import type { PriceListBuilderState, PriceListBuilderValidated } from '../schemas';
import { PRICE_LIST_SOURCES } from '../types';
import { usePaginatedPriceLists } from './use-price-lists';

/** Discount a template is seeded with; the merchant overrides it before the list is created. */
const DEFAULT_TEMPLATE_DISCOUNT = 20;

/**
 * A resolved platform price list plus its discounting defaults. `defaultDiscount` only seeds the
 * input; name/handle/amount are derived from the chosen value by {@link templateToBuilderState}.
 */
export type PriceListTemplate = {
  /** Slug of the platform price list this template derives from; the created list's source. */
  priceListSource: string;
  /** Display name of the source list, used to build the created list's name. */
  sourceName: string;
  direction: 'minus' | 'plus';
  currency: string;
  /** Seeds the discount input and the default construction; overridden per-use. */
  defaultDiscount: number;
};

/** i18n name for a template applied at `discount`, e.g. "30% off Wholesale". */
function templateName(template: PriceListTemplate, discount: number): string {
  return t('price_lists.empty_state.templates.percent_off', {
    percent: discount,
    name: template.sourceName,
  });
}

/** Slug-safe handle for a template applied at `discount`, e.g. "30-off-wholesale". */
function templateHandle(template: PriceListTemplate, discount: number): string {
  // A decimal discount would otherwise produce a "." that the slug schema rejects.
  return `${String(discount).replace('.', '-')}-off-${template.priceListSource}`;
}

export function usePriceListTemplates(): PriceListTemplate[] {
  const settings = useSettings();
  const { data: platformPriceLists } = usePaginatedPriceLists([PRICE_LIST_SOURCES.PLATFORM]);
  const baseCurrency = settings.baseCurrency;

  return useMemo(
    () =>
      platformPriceLists.map((list) => ({
        priceListSource: list.slug,
        sourceName: list.name,
        direction: 'minus' as const,
        currency: baseCurrency,
        defaultDiscount: DEFAULT_TEMPLATE_DISCOUNT,
      })),
    [platformPriceLists, baseCurrency],
  );
}

export function usePriceListTemplate(handle: string | undefined): PriceListTemplate | undefined {
  const templates = usePriceListTemplates();
  return useMemo(
    () => templates.find((tpl) => templateHandle(tpl, tpl.defaultDiscount) === handle),
    [handle, templates],
  );
}

/**
 * Applies the chosen discount to a template, producing validated builder state for the create
 * payload. Name, handle, and amount track `discount`, not the template's default.
 */
export function templateToBuilderState(
  template: PriceListTemplate,
  discount: number,
): PriceListBuilderValidated {
  return {
    name: templateName(template, discount),
    handle: templateHandle(template, discount),
    currency: template.currency,
    automaticPricing: {
      priceListSource: template.priceListSource,
      percentageVariation: {
        amount: discount,
        direction: template.direction,
      },
    },
  };
}

/**
 * Raw builder state seeded at {@link PriceListTemplate.defaultDiscount} to pre-fill the full builder
 * form. Use when opening the builder from a template rather than creating the list directly.
 */
export function templateToInitialState(template: PriceListTemplate): PriceListBuilderState {
  const discount = template.defaultDiscount;
  return {
    name: templateName(template, discount),
    handle: templateHandle(template, discount),
    currency: template.currency,
    automaticPricing: {
      priceListSource: template.priceListSource,
      percentageVariation: {
        amount: String(discount),
        direction: template.direction,
      },
    },
  };
}
